import { BCP47, formatAmountByLocale } from '@alk/numbers';
import { ColDef } from '@ag-grid-community/core';
import { formatDatetime } from '@alk/dates';
import { IOrderStatus, orderStatusMapper } from '.';
import { priceAndPointsValueFormatter } from '../blotter.service.utils';

const HARDCODED_FRACTION_SIZE_POINTS = 2;

const formatAmount = formatAmountByLocale(navigator.language as BCP47)(HARDCODED_FRACTION_SIZE_POINTS)(HARDCODED_FRACTION_SIZE_POINTS);

export const COL_DEF_DEAL_NUMBER: ColDef = {
    type: [ 'string' ],
    headerName: 'Deal Number',
    field: 'DealNumber',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 200,
    width: 200,
};

export const COL_DEF_ENTERED_BY: ColDef = {
    type: [ 'string' ],
    headerName: 'Entered By',
    field: 'Entered by',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_CPTY: ColDef = {
    type: [ 'string' ],
    headerName: 'Cpty',
    field: 'Cpty',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_SIDE: ColDef = {
    type: [ 'string' ],
    headerName: 'Side',
    field: 'B/S',
    headerClass: 'standard-cell',
    width: 55,
};

export const COL_DEF_DEAL_TYPE: ColDef = {
    type: [ 'string' ],
    headerName: 'Deal Type',
    field: 'Deal Type',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_MATURITY_DATE: ColDef = {
    type: [ 'date' ],
    headerName: 'Maturity Date',
    field: 'Maturity Date',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_PAIR: ColDef = {
    type: [ 'string' ],
    headerName: 'Pair',
    field: 'Pair',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 100,
    width: 100,
};

export const COL_DEF_DEALT_CCY: ColDef = {
    type: [ 'string' ],
    headerName: 'Dealt Ccy',
    field: 'Dealt Ccy',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 80,
    width: 80,
};

export const COL_DEF_DEALT_AMOUNT: ColDef = {
    type: [ 'number' ],
    headerName: 'Dealt Amount',
    field: 'Dealt Amount',
    valueFormatter: params => formatAmount(params?.value ?? 0),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_COUNTER_CCY: ColDef = {
    type: [ 'string' ],
    headerName: 'Counter Ccy',
    field: 'Counter Ccy',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 100,
    width: 100,
};

export const COL_DEF_COUNTER_AMOUNT: ColDef = {
    type: [ 'number' ],
    headerName: 'Counter Amount',
    field: 'Counter Amount',
    valueFormatter: params => formatAmount(params?.value ?? 0),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_SPOT_RATE: ColDef = {
    type: [ 'rate' ],
    headerName: 'Spot Rate',
    field: 'Spot rate',
    valueFormatter: params => priceAndPointsValueFormatter(params),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 90,
    width: 90,
};

export const COL_DEF_FX_POINTS: ColDef = {
    type: [ 'rate' ],
    headerName: 'FX Points',
    field: 'FX Points',
    valueFormatter: params => priceAndPointsValueFormatter(params),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 90,
    width: 90,
};

export const COL_DEF_FINAL_RATE: ColDef = {
    type: [ 'rate' ],
    headerName: 'Final Rate',
    field: 'Final rate',
    valueFormatter: params => priceAndPointsValueFormatter(params),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 90,
    width: 90,
};

export const COL_DEF_TRADE_DATE: ColDef = {
    type: [ 'date' ],
    headerName: 'Trade Date',
    field: 'Trade Date',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_FILLED_BY: ColDef = {
    type: [ 'string' ],
    headerName: 'Filled By',
    field: 'Filled by',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_FILLED_AT: ColDef = {
    type: [ 'date' ],
    headerName: 'Filled At',
    field: 'Filled at',
    valueFormatter: (params) => {
        return Boolean(params.value) ? formatDatetime(Date.parse(params.value)) : '';
    },
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 220,
    width: 220,
};

export const COL_DEF_SFX_ORDER_ID: ColDef = {
    type: [ 'string' ],
    headerName: 'SFX Order ID',
    field: 'SFX.OrderId',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_EXEC_DATE: ColDef = {
    type: [ 'date' ],
    headerName: 'Execution Date',
    field: 'Exect Date',
    valueFormatter: (params) => {
        return Boolean(params.value) ? formatDatetime(Date.parse(params.value)) : '';
    },
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 220,
    width: 220,
};

export const COL_DEF_LEG_TYPE: ColDef = {
    type: [ 'string' ],
    headerName: 'Leg Type',
    field: 'Leg Type',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_SPOT_MARGIN: ColDef = {
    type: [ 'rate' ],
    headerName: 'Spot Margin',
    field: 'Spot Margin',
    valueFormatter: params => priceAndPointsValueFormatter(params),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 90,
    width: 90,
};

export const COL_DEF_POINTS_MARGIN: ColDef = {
    type: [ 'rate' ],
    headerName: 'Points Margin',
    field: 'Points Margin',
    valueFormatter: params => priceAndPointsValueFormatter(params),
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 90,
    width: 90,
};

export const COL_DEF_COMMENT: ColDef = {
    type: [ 'string' ],
    headerName: 'Comment',
    field: 'Comment',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_STATUS: ColDef = {
    type: [ 'string' ],
    headerName: 'Status',
    field: 'Status',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 80,
    width: 80,
};

export const COL_DEF_LAST_MOD: ColDef = {
    type: [ 'sleeve' ],
    headerName: 'Last Modif.',
    field: 'Last Mod',
    cellRendererParams: {
        getSleeveValue: (value: IOrderStatus): string => orderStatusMapper[ value ],
        getSleeveClass: (value: string): string => `ag-sleeve-${ orderStatusMapper[ value ].toLowerCase() }`,
    },
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_LAST_MOD_TIME: ColDef = {
    type: [ 'date' ],
    headerName: 'Last Modif. Time',
    field: 'Last Mod Time',
    valueFormatter: (params) => {
        return Boolean(params.value) ? formatDatetime(Date.parse(params.value)) : '';
    },
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 220,
    width: 220,
    sort: 'desc',
};

export const COL_DEF_LAST_MOD_BY: ColDef = {
    type: [ 'string' ],
    headerName: 'Last Modif. By',
    field: 'Last Mod by',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};

export const COL_DEF_CLIENTCOD: ColDef = {
    type: [ 'string' ],
    headerName: 'ClientCOD',
    field: 'ClientCOD',
    headerClass: 'standard-cell',
    resizable: true,
    minWidth: 120,
    width: 120,
};
